import { create } from 'zustand';
import { sanitizeData, checkPayloadSize, sanitizeHeaders } from '../utils/debugUtils';

export interface ApiCall {
  id: string;
  url: string;
  method: string;
  status?: number;
  statusText?: string;
  requestHeaders?: Record<string, any>;
  requestData?: any;
  responseData?: any;
  error?: any;
  startTime: number;
  endTime?: number;
  duration?: number;
  timestamp: Date;
  createdAt: number; // For age-based cleanup
}

interface ApiDebugStore {
  calls: ApiCall[];
  maxCalls: number;
  maxAge: number; // Max age in milliseconds
  isEnabled: boolean;
  addCall: (call: Omit<ApiCall, 'id' | 'timestamp' | 'createdAt'>) => string;
  updateCall: (id: string, updates: Partial<ApiCall>) => void;
  clearCalls: () => void;
  setEnabled: (enabled: boolean) => void;
  exportCalls: () => string;
  cleanupOldCalls: () => void;
}

export const useApiDebugStore = create<ApiDebugStore>((set, get) => ({
  calls: [],
  maxCalls: 100,
  maxAge: 15 * 60 * 1000, // 15 minutes
  isEnabled: true,
  
  addCall: (call) => {
    const id = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    // Sanitize and check payload sizes
    const sanitizedHeaders = sanitizeHeaders(call.requestHeaders || {});
    const sanitizedRequestData = sanitizeData(call.requestData);
    const { data: checkedRequestData } = checkPayloadSize(sanitizedRequestData);
    
    const newCall: ApiCall = {
      ...call,
      id,
      timestamp: new Date(),
      createdAt: Date.now(),
      requestHeaders: sanitizedHeaders,
      requestData: checkedRequestData,
    };
    
    set((state) => {
      // Clean up old calls before adding new one
      const now = Date.now();
      const filteredCalls = state.calls.filter(
        c => now - c.createdAt < state.maxAge
      );
      
      return {
        calls: [newCall, ...filteredCalls].slice(0, state.maxCalls),
      };
    });
    
    return id;
  },
  
  updateCall: (id, updates) => {
    set((state) => ({
      calls: state.calls.map((call) => {
        if (call.id !== id) return call;
        
        // Sanitize response data and error
        const sanitizedUpdates = { ...updates };
        
        if (updates.responseData !== undefined) {
          const sanitized = sanitizeData(updates.responseData);
          const { data: checked } = checkPayloadSize(sanitized);
          sanitizedUpdates.responseData = checked;
        }
        
        if (updates.error !== undefined) {
          sanitizedUpdates.error = sanitizeData(updates.error);
        }
        
        if (updates.responseHeaders !== undefined) {
          sanitizedUpdates.responseHeaders = sanitizeHeaders(updates.responseHeaders);
        }
        
        return {
          ...call,
          ...sanitizedUpdates,
          duration: updates.endTime
            ? updates.endTime - call.startTime
            : call.duration,
        };
      }),
    }));
  },
  
  clearCalls: () => {
    set({ calls: [] });
  },
  
  setEnabled: (enabled) => {
    set({ isEnabled: enabled });
  },
  
  exportCalls: () => {
    const { calls } = get();
    return JSON.stringify(calls, null, 2);
  },
  
  cleanupOldCalls: () => {
    set((state) => {
      const now = Date.now();
      const filteredCalls = state.calls.filter(
        call => now - call.createdAt < state.maxAge
      );
      return { calls: filteredCalls };
    });
  },
}));